import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  mockKPIs,
  mockAgentPerformances,
  mockTeamPerformances,
  KPI,
  KPIStatus,
  KPIPeriod,
  getStatusColor,
  getStatusLabel,
  getStatusIcon,
  getPeriodLabel,
} from '../services/mockKPIs';
import { useNavigate } from 'react-router-dom';

const KPITracking: React.FC = () => {
  const { user } = useAuth();
  const { hasPermission, isAdmin, isAgence, isSOBEBRA, isAgentTerrain } = usePermissions();
  const navigate = useNavigate();
  const [kpis, setKPIs] = useState<KPI[]>(mockKPIs);
  const [tabValue, setTabValue] = useState(0);
  const [newKPIDialogOpen, setNewKPIDialogOpen] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState<KPI | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const canCreateKPI = () => {
    return isAdmin || isAgence || isSOBEBRA;
  };

  const canViewAllKPIs = () => {
    return isAdmin || isAgence || isSOBEBRA;
  };

  const filteredKPIs = canViewAllKPIs()
    ? kpis
    : kpis.filter((kpi) => {
        // Agents voient uniquement les KPIs liés à leurs projets
        return true; // Simplifié pour la démo
      });

  const getKPIsByStatus = (status: KPIStatus) => {
    return filteredKPIs.filter((kpi) => kpi.status === status).length;
  };

  const getAverageProgress = () => {
    if (filteredKPIs.length === 0) return 0;
    const sum = filteredKPIs.reduce((acc, kpi) => acc + kpi.progress_percentage, 0);
    return Math.round(sum / filteredKPIs.length);
  };

  const getDaysRemaining = (endDate: string): number => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Suivi des Objectifs et Performance (KPIs)
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Pilotage stratégique et suivi automatique de la progression des objectifs
            </Typography>
          </Box>
          {canCreateKPI() && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewKPIDialogOpen(true)}>
              Nouvel Objectif
            </Button>
          )}
        </Box>

        {/* Statistiques globales */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Objectifs
                </Typography>
                <Typography variant="h4">{filteredKPIs.length}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Sur la bonne voie
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {getKPIsByStatus('on_track')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="warning" />
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      À risque
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {getKPIsByStatus('at_risk')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Progression Moyenne
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h4">{getAverageProgress()}%</Typography>
                  <TrendingUpIcon color="primary" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Onglets */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Tableau de Bord KPIs" />
            <Tab label="Classement Agents" />
            <Tab label="Performance Équipes" />
          </Tabs>
        </Paper>

        {/* Tableau de Bord KPIs */}
        {tabValue === 0 && (
          <Grid container spacing={3}>
            {filteredKPIs.map((kpi) => {
              const daysRemaining = getDaysRemaining(kpi.end_date);
              const isOverdue = daysRemaining < 0;

              return (
                <Grid item xs={12} md={6} key={kpi.id}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 6 },
                    }}
                    onClick={() => {
                      setSelectedKPI(kpi);
                      setDetailDialogOpen(true);
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" component="h3" gutterBottom>
                            {kpi.name}
                          </Typography>
                          {kpi.description && (
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                              {kpi.description}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={getStatusIcon(kpi.status)}
                          color={getStatusColor(kpi.status)}
                          size="small"
                          sx={{ fontSize: '1.2rem', height: 'auto', py: 0.5 }}
                        />
                      </Box>

                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="textSecondary">
                            Progression
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {kpi.progress_percentage.toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(kpi.progress_percentage, 100)}
                          color={getStatusColor(kpi.status)}
                          sx={{ height: 8, borderRadius: 1 }}
                        />
                      </Box>

                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Actuel
                          </Typography>
                          <Typography variant="h6">
                            {kpi.current_value} {kpi.unit}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="textSecondary">
                            Objectif
                          </Typography>
                          <Typography variant="h6">
                            {kpi.target_value} {kpi.unit}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                          label={getStatusLabel(kpi.status)}
                          color={getStatusColor(kpi.status)}
                          size="small"
                        />
                        <Typography variant="caption" color="textSecondary">
                          {getPeriodLabel(kpi.period)} • {daysRemaining >= 0 ? `${daysRemaining} jours restants` : 'Échu'}
                        </Typography>
                      </Box>

                      {kpi.linked_project_name && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" color="textSecondary">
                            Projet lié:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {kpi.linked_project_name}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Classement Agents */}
        {tabValue === 1 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Rang</TableCell>
                  <TableCell>Agent</TableCell>
                  <TableCell>Rapports</TableCell>
                  <TableCell>Projets</TableCell>
                  <TableCell>Sondages</TableCell>
                  <TableCell align="right">Score KPI</TableCell>
                  <TableCell>Réalisations</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mockAgentPerformances.map((agent) => (
                  <TableRow key={agent.agent_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {agent.rank === 1 && <TrophyIcon sx={{ color: '#FFD700' }} />}
                        {agent.rank === 2 && <TrophyIcon sx={{ color: '#C0C0C0' }} />}
                        {agent.rank === 3 && <TrophyIcon sx={{ color: '#CD7F32' }} />}
                        <Typography variant="h6">{agent.rank}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                          {agent.agent_name.charAt(0)}
                        </Avatar>
                        <Typography variant="body1">{agent.agent_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{agent.total_reports}</TableCell>
                    <TableCell>{agent.completed_projects}</TableCell>
                    <TableCell>{agent.survey_responses}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Typography variant="h6" color="primary">
                          {agent.kpi_score}
                        </Typography>
                        <StarIcon sx={{ color: '#FFD700', fontSize: '1rem' }} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {agent.achievements.map((achievement, idx) => (
                          <Chip key={idx} label={achievement} size="small" color="primary" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Performance Équipes */}
        {tabValue === 2 && (
          <Grid container spacing={3}>
            {mockTeamPerformances.map((team) => (
              <Grid item xs={12} md={6} key={team.team_id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">{team.team_name}</Typography>
                      <Chip label={`Rang #${team.rank}`} color="primary" />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          Progression Moyenne
                        </Typography>
                        <Typography variant="h6">{team.average_progress.toFixed(1)}%</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={team.average_progress}
                        color="primary"
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
                          <Typography variant="h6">{team.kpis_on_track}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Sur la bonne voie
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <WarningIcon color="warning" sx={{ fontSize: 32 }} />
                          <Typography variant="h6">{team.kpis_at_risk}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            À risque
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <ErrorIcon color="error" sx={{ fontSize: 32 }} />
                          <Typography variant="h6">{team.kpis_off_track}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Hors piste
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Dialog Nouvel Objectif */}
      <Dialog open={newKPIDialogOpen} onClose={() => setNewKPIDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Créer un Nouvel Objectif (KPI)</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Nom de l'objectif" required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" multiline rows={3} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select label="Type">
                  <MenuItem value="reports_count">Nombre de rapports</MenuItem>
                  <MenuItem value="survey_results">Résultats sondages</MenuItem>
                  <MenuItem value="projects_completed">Projets terminés</MenuItem>
                  <MenuItem value="brand_visibility">Visibilité marque</MenuItem>
                  <MenuItem value="customer_satisfaction">Satisfaction client</MenuItem>
                  <MenuItem value="custom">Personnalisé</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Période</InputLabel>
                <Select label="Période">
                  <MenuItem value="daily">Quotidien</MenuItem>
                  <MenuItem value="weekly">Hebdomadaire</MenuItem>
                  <MenuItem value="monthly">Mensuel</MenuItem>
                  <MenuItem value="quarterly">Trimestriel</MenuItem>
                  <MenuItem value="yearly">Annuel</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Valeur cible" type="number" required />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Unité" placeholder="%, nombre, €, etc." />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Date de début" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Date de fin" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewKPIDialogOpen(false)}>Annuler</Button>
          <Button variant="contained">Créer l'Objectif</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Détails KPI */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedKPI?.name}
          {selectedKPI && (
            <Chip
              label={getStatusLabel(selectedKPI.status)}
              color={getStatusColor(selectedKPI.status)}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedKPI && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Description
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedKPI.description || 'Aucune description'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Valeur actuelle
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {selectedKPI.current_value} {selectedKPI.unit}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Valeur cible
                  </Typography>
                  <Typography variant="h4">
                    {selectedKPI.target_value} {selectedKPI.unit}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        Progression
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedKPI.progress_percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(selectedKPI.progress_percentage, 100)}
                      color={getStatusColor(selectedKPI.status)}
                      sx={{ height: 10, borderRadius: 1 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Période
                  </Typography>
                  <Typography variant="body1">
                    {getPeriodLabel(selectedKPI.period)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Date de fin
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedKPI.end_date).toLocaleDateString('fr-FR')}
                  </Typography>
                </Grid>
                {selectedKPI.linked_project_name && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Projet lié
                    </Typography>
                    <Typography variant="body1">
                      {selectedKPI.linked_project_name}
                    </Typography>
                  </Grid>
                )}
                {selectedKPI.zone && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Zone
                    </Typography>
                    <Typography variant="body1">
                      {selectedKPI.zone}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default KPITracking;





